import { z } from "zod";
import { PlayerWalletSchema } from "./entities.js";
import { BUILDING_FAMILIES, type BuildingFamily } from "./enums.js";

/** §5.1 / §6.4.3 construction lifecycle (Phase 2.C). */
export const CONSTRUCTION_STATES = ["active", "complete"] as const;
export type ConstructionState = (typeof CONSTRUCTION_STATES)[number];
export const ConstructionStateSchema = z.enum(CONSTRUCTION_STATES);

const buildingFamilyEnum = z.enum(
  BUILDING_FAMILIES as readonly [BuildingFamily, ...BuildingFamily[]],
);

export const ConstructionJobSchema = z.object({
  id: z.string().uuid(),
  baseBuildingId: z.string().uuid(),
  family: buildingFamilyEnum,
  targetLevel: z.number().int().positive(),
  startedAtMs: z.number().int().nonnegative(),
  durationMs: z.number().int().positive(),
  state: ConstructionStateSchema,
});
export type ConstructionJobDto = z.infer<typeof ConstructionJobSchema>;

/** POST /api/buildings/:id/upgrade */
export const UpgradeBuildingRequestSchema = z.object({
  baseBuildingId: z.string().uuid(),
});
export type UpgradeBuildingRequest = z.infer<typeof UpgradeBuildingRequestSchema>;

export const UpgradeBuildingResponseSchema = z.object({
  ok: z.literal(true),
  job: ConstructionJobSchema,
  wallet: PlayerWalletSchema,
});
export type UpgradeBuildingResponse = z.infer<typeof UpgradeBuildingResponseSchema>;

/** GET /api/buildings/construction/list?baseId= */
export const ConstructionListQuerySchema = z.object({
  baseId: z.string().uuid(),
});
export type ConstructionListQuery = z.infer<typeof ConstructionListQuerySchema>;

export const ConstructionListResponseSchema = z.object({
  baseId: z.string().uuid(),
  serverNowMs: z.number().int().nonnegative(),
  jobs: z.array(ConstructionJobSchema),
});
export type ConstructionListResponse = z.infer<typeof ConstructionListResponseSchema>;

/* ── Phase 2.D: speed-up flow ───────────────────────────────────────────── */

/** POST /api/buildings/speedup */
export const SpeedupBuildingRequestSchema = z.object({
  jobId: z.string().uuid(),
});
export type SpeedupBuildingRequest = z.infer<typeof SpeedupBuildingRequestSchema>;

/**
 * POST /api/buildings/speedup response — Phase 2.D.
 * Mirrors the production speed-up response shape: post-speedup job snapshot,
 * refreshed wallet, server-authoritative diamond cost.
 */
export const SpeedupBuildingResponseSchema = z.object({
  ok: z.literal(true),
  job: ConstructionJobSchema,
  wallet: PlayerWalletSchema,
  costDiamonds: z.number().int().nonnegative(),
});
export type SpeedupBuildingResponse = z.infer<typeof SpeedupBuildingResponseSchema>;
